import { TireClassification, TireQuoteItemRecordCustomer, TireQuoteRecordCustomer } from '@navio/nsdk';
import { getTirePrice } from 'noddi-util';

import { BulkStrategy } from '../../../../stores/tireOfferPickerStore';

/** Sum of tire prices × default quantities for a single quote-item option. */
function getTireOptionSubtotal(quoteItem: TireQuoteItemRecordCustomer): number {
  return quoteItem.quoteItemTires.reduce(
    (sum, tire) => sum + getTirePrice(tire).amount * (tire.quantityDefault ?? 0),
    0
  );
}

/** Sum of generic (non-tire) sales-item gross prices on the offer — fitting, mounting, etc. */
export function getGenericItemsTotal(offer: TireQuoteRecordCustomer): number {
  return offer.genericItems.reduce((sum, item) => sum + item.price.priceGross.amount, 0);
}

/** Full price the customer pays for accepting this option: tire subtotal + generic items. */
export function getOfferTotalForOption(offer: TireQuoteRecordCustomer, quoteItem: TireQuoteItemRecordCustomer): number {
  return getTireOptionSubtotal(quoteItem) + getGenericItemsTotal(offer);
}

const TIER_ORDER: Record<string, number> = {
  [TireClassification.BUDGET]: 0,
  [TireClassification.ECONOMY]: 0,
  [TireClassification.QUALITY]: 1,
  [TireClassification.PREMIUM]: 2,
  [TireClassification.FIXED_COST]: 3,
  [TireClassification.UNCLASSIFIED]: 3
};

function getTierOrder(quoteItem: TireQuoteItemRecordCustomer): number {
  const classification = quoteItem.quoteItemTires[0]?.tireInventory.tire.classification;
  return classification ? (TIER_ORDER[classification] ?? 3) : 3;
}

export function sortQuoteItemsByTier(items: TireQuoteItemRecordCustomer[]): TireQuoteItemRecordCustomer[] {
  return [...items].sort((a, b) => {
    const tierDiff = getTierOrder(a) - getTierOrder(b);
    if (tierDiff !== 0) {
      return tierDiff;
    }
    // Within the same tier, fall back to total price ascending so the cheaper variant comes first.
    return getTireOptionSubtotal(a) - getTireOptionSubtotal(b);
  });
}

function pickQuoteItemForStrategy(
  offer: TireQuoteRecordCustomer,
  strategy: BulkStrategy
): TireQuoteItemRecordCustomer | undefined {
  const items = offer.quoteItems;
  if (items.length === 0) {
    return undefined;
  }

  const sortedByPrice = [...items].sort((a, b) => getTireOptionSubtotal(a) - getTireOptionSubtotal(b));

  switch (strategy) {
    case 'cheapest':
      return sortedByPrice[0];
    case 'premium':
      return sortedByPrice[sortedByPrice.length - 1];
    case 'mid':
      return sortedByPrice[Math.floor(sortedByPrice.length / 2)] ?? sortedByPrice[0];
    case 'recommended': {
      const recommended = items.find((item) => item.isRecommended);
      if (recommended) {
        return recommended;
      }
      const standard = items.find(
        (item) => item.quoteItemTires[0]?.tireInventory.tire.classification === TireClassification.QUALITY
      );
      return standard ?? sortedByPrice[0];
    }
  }
}

export function applyStrategyToOffers(
  offers: TireQuoteRecordCustomer[],
  strategy: BulkStrategy
): Record<number, number> {
  return offers.reduce<Record<number, number>>((acc, offer) => {
    const pick = pickQuoteItemForStrategy(offer, strategy);
    if (pick) {
      acc[offer.id] = pick.id;
    }
    return acc;
  }, {});
}
